/**
 * informe-editor-server-id.ts — pure helpers for serverId/localId resolution in InformeEditor.
 *
 * The root bug (Fix 1) is that the editor derived serverId by comparing informe.id
 * to the route param id. When the route is /informe/<serverId>, both are equal and
 * the derivation always returns undefined — poisoning guardar(), autosave, and finalizar().
 *
 * These helpers are extracted for testability. InformeEditor calls them during the
 * load sequence and stores the results in React state (resolvedServerId, resolvedLocalId).
 */

import type { LocalReport } from "@/features/offline-sync/types.js";

export interface ServerIdResolution {
  /** The server-assigned id, or undefined for reports that have never been synced. */
  serverId: string | undefined;
  /**
   * The IDB localId to use for putReport calls.
   * - If an IDB record already exists for this serverId: its originalLocalId.
   * - If the report was loaded from the network with no prior IDB record:
   *   falls back to the network id (= route param = serverId) as a placeholder.
   *   This is safe because putReport writes serverId too, so the record is not a ghost.
   */
  localId: string;
}

/**
 * Resolve serverId and localId from a successful network load (getInforme succeeded).
 *
 * The network response id IS the server id (= route param). If the panel previously
 * cached this report in IDB, use its originalLocalId for writes to avoid duplicates.
 *
 * @param networkId     informe.id from getInforme() — equals the route param, always a serverId
 * @param cachedRecord  IDB record found via getReportByServerId(networkId), may be absent
 */
export function resolveFromNetworkLoad(
  networkId: string,
  cachedRecord: LocalReport | undefined,
): ServerIdResolution {
  return {
    serverId: networkId,
    localId: cachedRecord?.localId ?? networkId,
  };
}

/**
 * Resolve serverId and localId from an IDB fallback load (getInforme failed / offline).
 * The IDB record is the authoritative source for both fields.
 *
 * @param localRecord  IDB record found via getReport(id) or getReportByServerId(id)
 */
export function resolveFromIdbLoad(localRecord: LocalReport): ServerIdResolution {
  return {
    serverId: localRecord.serverId, // undefined for reports that have never synced
    localId: localRecord.localId,
  };
}
